import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { Select, MenuItem, IconButton, Skeleton } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import { Clear } from '@mui/icons-material';
import { DashboardPanelContext } from '../../context/DashboardContext';
import { convertKeyedObjectToArray, ReactFormHookServerError } from '../../../common/utilities';
import BInputLabel from '../../BInputLabel';
import BErrorFormHelperText from '../../BErrorFormHelperText';

const checkedIcon = <CheckBoxIcon fontSize='small' />;
const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;

const BSelectField = ({
  items = {},
  label,
  name,
  value,
  readOnly = false,
  error,
  onChange,
  onKeyDown,
  serverError,
  required = false,
  xs = 12,
  sm = 4,
  multiple = false,
}) => {
  const {
    globalState: { pageLoading },
  } = useContext(DashboardPanelContext);
  const prepareForOnChange = (e) => {
    const itm = {
      currentTarget: {
        name,
        value: e.target.value,
      },
    };

    if (onChange) onChange(itm);
  };

  const prepareForOnChangeMultiple = (e, d) => {
    const itm = {
      currentTarget: {
        name,
        value: d.map((c) => c.key),
      },
    };

    if (onChange) onChange(itm);
  };

  const [multipleOptions, setMultipleOptions] = useState([]);
  const [_value, setValue] = useState([]);
  useEffect(() => {
    setMultipleOptions(convertKeyedObjectToArray(items, 'key', 'value'));
  }, [items]);

  useEffect(() => {
    if (value && multiple && multipleOptions.length > 0) {
      const result = [];
      value.forEach((e) => {
        const f = multipleOptions.find((c) => c.key === e);
        result.push(f);
      });
      setValue(result);
    }
  }, [value, multipleOptions]);

  const clearSelect = () =>
    prepareForOnChange({
      target: {
        name,
        value: undefined,
      },
    });

  const renderInput = (params) => (
    <TextField
      error={error?.message || ReactFormHookServerError(serverError, 'messages', 'fieldName', name)}
      {...params}
      name={name}
      fullWidth
      variant='outlined'
      value={params}
    />
  );

  const renderOption = (option, { selected }) => (
    <>
      <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
      {option.value}
    </>
  );

  const getOptionLabel = (option) => option?.value;
  const getOptionSelected = (option, __value) => option?.key === __value?.key;
  const renderOptions = () =>
    Object.keys(items).map((item) => (
      <MenuItem key={item} id={`${name}-option-${item}`} value={item}>
        {items[item]}
      </MenuItem>
    ));

  const renderClearIcon = () => {
    if (value && !readOnly)
      return (
        <IconButton
          focusRipple={false}
          size='small'
          onClick={clearSelect}
          style={{ position: 'absolute', left: '25px', top: '12px' }}
        >
          <Clear />
        </IconButton>
      );
    return <></>;
  };

  const autoCompleteMultipleProps = {
    multiple,
    id: name,
    value: _value,
    options: multipleOptions,
    disableCloseOnSelect: true,
    disabled: readOnly,
    onChange: prepareForOnChangeMultiple,
    getOptionSelected,
    getOptionLabel,
    renderOption,
    renderInput,
  };

  const selectProps = {
    id: name,
    error: error?.message || ReactFormHookServerError(serverError, 'messages', 'fieldName', name),
    name,
    onKeyDown: onKeyDown || (() => undefined),
    onChange: prepareForOnChange,
    fullWidth: true,
    variant: 'outlined',
    value: items[value] ? value : '',
    disabled: readOnly,
  };

  return (
    <>
      <Grid item xs={xs} sm={sm} aria-disabled={readOnly} style={{ direction: 'rtl' }}>
        {pageLoading && Object.keys(items).length === 0 ? (
          <>
            <Typography variant='h1'>
              <Skeleton height={100} />
            </Typography>
          </>
        ) : (
          <>
            <BInputLabel label={label} error={error} name={name} required={required} serverError={serverError} />
            {!multiple && (
              <div style={{ position: 'relative' }}>
                <Select {...selectProps}>{renderOptions()}</Select>
                {/* {renderClearIcon()} */}
                <BErrorFormHelperText text={error?.message} />
                {ReactFormHookServerError(serverError, 'messages', 'fieldName', name)}
              </div>
            )}
            {multiple && (
              <>
                <Autocomplete {...autoCompleteMultipleProps} />
                <BErrorFormHelperText text={error?.message} />
                {ReactFormHookServerError(serverError, 'messages', 'fieldName', name)}
              </>
            )}
          </>
        )}
      </Grid>
    </>
  );
};

BSelectField.propTypes = {
  items: PropTypes.object.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  readOnly: PropTypes.bool,
  error: PropTypes.object,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  serverError: PropTypes.array,
  xs: PropTypes.number,
  sm: PropTypes.number,
  multiple: PropTypes.bool,
  required: PropTypes.bool,
};
export default React.memo(BSelectField);
